import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { EduVEmployee } from '../types';
import {
    KnownEduVConfig,
    forgetEduVConfig,
    getKnownEduVConfigs,
} from '../utils/eduVConfigs';
import { logger } from '../utils/logger';

interface ConfigSectionState {
    /** Opgeslagen employee_user_id; null zolang er geen koppeling is */
    employeeId: string | null;
    overridable: boolean;
    /** Sommige backends staan alleen het id toe; dan het type niet meesturen (400) */
    typeOverridable: boolean;
    employees: EduVEmployee[] | null;
    loading: boolean;
    saving: boolean;
    error: string | null;
    saved: boolean;
}

/**
 * "Mijn medewerker in het onderwijssysteem"-koppeling op de
 * instellingenpagina. Rendert niets zolang deze gebruiker nog geen
 * Edu-V-configuratie is tegengekomen (bekend via localStorage, gevuld door de
 * deel-dialoog). Eén schoolconfiguratie kan organisatiebreed gedeeld zijn
 * terwijl elke docent hier de eigen medewerker-identificatie zet; die gaat mee
 * bij een LAS_WEBHOOK-export.
 */
const EduVUserSettings: React.FC = () => {
    const { t } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    const [configs, setConfigs] = useState<KnownEduVConfig[]>(() => getKnownEduVConfigs());
    const [sections, setSections] = useState<Record<string, ConfigSectionState>>({});

    const patchSection = (configId: string, patch: Partial<ConfigSectionState>) => {
        const defaults: ConfigSectionState = {
            employeeId: null,
            overridable: false,
            typeOverridable: false,
            employees: null,
            loading: true,
            saving: false,
            error: null,
            saved: false,
        };
        setSections((prev) => ({
            ...prev,
            [configId]: { ...defaults, ...prev[configId], ...patch },
        }));
    };

    useEffect(() => {
        let cancelled = false;

        configs.forEach(async (config) => {
            patchSection(config.id, { loading: true });
            try {
                const settings = await apiService.getExportUserSettings(config.id);
                if (cancelled) return;
                const overridable = settings.overridableFields.includes('employee_user_id');
                const rawId = settings.settings?.employee_user_id;
                patchSection(config.id, {
                    overridable,
                    typeOverridable: settings.overridableFields.includes('employee_user_id_type'),
                    employeeId: typeof rawId === 'string' && rawId !== '' ? rawId : null,
                });
                if (!overridable) {
                    patchSection(config.id, { loading: false });
                    return;
                }
                // De medewerkerslijst komt via de backend uit het LAS; dat kan
                // even duren, vandaar de spinner per configuratie.
                const employees = await apiService.getEduVEmployees(config.id);
                if (cancelled) return;
                patchSection(config.id, { employees, loading: false });
            } catch (error: any) {
                if (cancelled) return;
                const status = error?.response?.status;
                logger.error('Edu-V user-settings load failed with status:', status);
                if (status === 404 || status === 403) {
                    // Configuratie bestaat niet meer (of is niet meer van deze
                    // gebruiker): vergeten en de sectie verbergen.
                    forgetEduVConfig(config.id);
                    setConfigs((prev) => prev.filter((item) => item.id !== config.id));
                    return;
                }
                patchSection(config.id, {
                    loading: false,
                    error: t('userSettings.eduv.loadError'),
                });
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * De optiewaarde is de index in de lijst, niet het id: dat id kan een ECK
     * iD zijn en hoort niet in de DOM te belanden.
     */
    const saveEmployee = async (configId: string, indexValue: string) => {
        const section = sections[configId];
        const employee =
            indexValue === '' ? null : section?.employees?.[Number(indexValue)] ?? null;
        if (indexValue !== '' && !employee) return;
        patchSection(configId, { saving: true, error: null, saved: false });
        try {
            // null wist de override; het id-type hoort altijd bij de waarde.
            await apiService.updateExportUserSettings(configId, {
                employee_user_id: employee ? employee.id : null,
                ...(section?.typeOverridable
                    ? { employee_user_id_type: employee?.idType ?? null }
                    : {}),
            });
            patchSection(configId, {
                employeeId: employee ? employee.id : null,
                saving: false,
                saved: true,
            });
        } catch (error: any) {
            logger.error('Edu-V user-settings save failed with status:', error?.response?.status);
            patchSection(configId, {
                saving: false,
                error: t('userSettings.eduv.saveError'),
            });
        }
    };

    // Alleen configuraties tonen waar de koppeling ook echt kan (of nog laadt).
    const visibleConfigs = configs.filter((config) => {
        const section = sections[config.id];
        return !section || section.loading || section.overridable;
    });

    if (visibleConfigs.length === 0) {
        return null;
    }

    return (
        <div className="mb-6">
            <div className="flex items-center mb-4">
                <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422A12.083 12.083 0 0112 20.055a12.083 12.083 0 01-6.16-9.477L12 14z" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900">{t('userSettings.eduv.heading')}</h2>
            </div>

            <div className="space-y-4">
                {visibleConfigs.map((config) => {
                    const section = sections[config.id];
                    const employees = section?.employees;
                    const selectedIndex = employees && section?.employeeId
                        ? employees.findIndex((employee) => employee.id === section.employeeId)
                        : -1;
                    // Opgeslagen koppeling die niet in de lijst voorkomt: de
                    // dropdown zou anders ten onrechte "geen koppeling" tonen.
                    const savedButUnknown =
                        !!section?.employeeId && !!employees && selectedIndex === -1;
                    return (
                        <div key={config.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {visibleConfigs.length > 1 && config.name
                                    ? t('userSettings.eduv.labelWithConfig', { name: config.name })
                                    : t('userSettings.eduv.label')}
                            </label>
                            {!section || section.loading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <div className="w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                                    {t('userSettings.eduv.loading')}
                                </div>
                            ) : (
                                <>
                                    {employees && (
                                        <select
                                            value={selectedIndex >= 0 ? String(selectedIndex) : ''}
                                            onChange={(e) => saveEmployee(config.id, e.target.value)}
                                            disabled={section.saving}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-600 focus:border-primary-600 disabled:opacity-50"
                                        >
                                            <option value="">{t('userSettings.eduv.none')}</option>
                                            {employees.map((employee, index) => (
                                                <option key={index} value={index}>
                                                    {/* Edu-V kent hier geen primaire
                                                        ketenidentifier: extra velden zijn
                                                        het enige onderscheid bij naamgenoten. */}
                                                    {[employee.name || t('userSettings.eduv.unnamed'), employee.details]
                                                        .filter(Boolean)
                                                        .join(' — ')}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {savedButUnknown && !section.saving && (
                                        <p className="mt-1 text-xs text-amber-700">
                                            {t('userSettings.eduv.unknownSelection')}
                                        </p>
                                    )}
                                    {section.saved && !section.saving && (
                                        <p className="mt-1 text-xs text-green-600">
                                            {t('userSettings.eduv.saved')}
                                        </p>
                                    )}
                                    {section.error && (
                                        <p className="mt-1 text-xs text-red-600">{section.error}</p>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
                <p className="text-xs text-gray-500">{t('userSettings.eduv.hint')}</p>
            </div>
        </div>
    );
};

export default EduVUserSettings;
